import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import {
  PageContainer,
  PageHeader,
  PageTitle,
  PageContent,
  Section,
  SectionHeader,
  SectionTitle,
  BannerContent,
  BannerForm,
  TitleRow,
  TitleInput,
  ColorInputWrapper,
  ColorPicker,
  ColorTextInput,
  CaptionRow,
  CaptionInput,
  SectionActions,
  ErrorMessage,
  LoadingMessage,
  CurationSet,
  CurationForm,
  CurationFormGroup,
  CurationButtons,
  CurationSectionHeader,
  CurationSectionTitle,
  CurationSectionDescription,
  CurationSectionActions,
  LectureGrid,
  LectureCard,
  LectureCardImage,
  NoImage,
  LectureCardContent,
  LectureTitle,
  LecturerName,
  RemoveLectureButton,
  AddLectureCard,
  LectureModal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  CategoryModal,
  ModalTitle,
  CloseButton,
  CategoryModalBody,
  FormGroup,
  FormLabel,
  FormInput,
  SelectWrapper,
  FormSelect,
  ImageUploadSection,
  ImageUploadGroup,
  ImageUploadBox,
  ModalFooter,
  DeleteButton,
  SaveButton,
  LectureList,
  LectureListItem,
  LectureListImage,
  LectureListContent,
  LectureTags,
  Tag,
  AddedIndicator,
  ImageUpload,
  ImagePlaceholder,
  UploadIcon,
  UploadedImage,
  InputField,
  Input,
  ButtonBase,
  CategorySection as CategorySectionContainer,
  AddCategoryButton,
  TableContainer,
  TableInner,
  TableHead,
  TableHeadRow,
  TableBody,
  TableBodyRow,
  CellContainer,
  CellText,
  HeaderCellText,
  CellOrder,
  CellName,
  CellParent,
  CellCount,
  CellAction,
  TableButton,
  TableButtonDelete,
} from './ClassManagementStyles';
import {
  getLectureCategories,
  getLectureList,
  createBanner,
  getBannerList,
  updateBanner,
  deleteBanner,
  getLectureCurationList,
  createLectureCurationSection,
  deleteLectureCurationSection,
  addLectureToCuration,
  removeLectureFromCuration,
  updateLectureCategory,
  deleteLectureCategory,
  createLectureCategory,
  updateCategoryOrder,
  updateCategoriesOrder,
} from '../../../services/admin/classApi';
import {
  LectureCategory,
  LectureListItem as LectureListItemType,
} from '../../../types/admin';

// 타입 정의
interface ImageUploadProps {
  className?: string;
  onImageChange?: (imageUrl: string | null, file?: File) => void;
  imageUrl?: string;
}

interface InputFieldComponentProps {
  placeholder: string;
  type?: string;
  className?: string;
}

interface ButtonProps {
  children: React.ReactNode;
  variant?: 'outlined' | 'primary' | 'text';
  onClick?: (e?: React.MouseEvent) => void;
  className?: string;
  disabled?: boolean;
  style?: React.CSSProperties;
}

// 배너 플러스 아이콘 (32x32, #448181 색상)
const BannerPlusIcon: React.FC = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
    <path d="M15.9994 4.13725C16.6622 4.13725 17.1994 4.67451 17.1994 5.33725V14.8039H26.6661C27.3288 14.8039 27.8661 15.3412 27.8661 16.0039C27.8661 16.6667 27.3288 17.2039 26.6661 17.2039H17.1994V26.6706C17.1994 27.3333 16.6622 27.8706 15.9994 27.8706C15.3367 27.8706 14.7994 27.3333 14.7994 26.6706V17.2039H5.33281C4.67007 17.2039 4.13281 16.6667 4.13281 16.0039C4.13281 15.3412 4.67007 14.8039 5.33281 14.8039H14.7994V5.33725C14.7994 4.67451 15.3367 4.13725 15.9994 4.13725Z" fill="#448181"/>
  </svg>
);

// 플러스 아이콘 (24x24로 스케일 조정, #448181 색상 - 제공된 SVG 사용)
const PlusIcon: React.FC = () => (
  <svg width="24" height="24" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M15.9994 4.13721C16.6622 4.13721 17.1994 4.67447 17.1994 5.33721V14.8039H26.6661C27.3288 14.8039 27.8661 15.3411 27.8661 16.0039C27.8661 16.6666 27.3288 17.2039 26.6661 17.2039H17.1994V26.6705C17.1994 27.3333 16.6622 27.8705 15.9994 27.8705C15.3367 27.8705 14.7994 27.3333 14.7994 26.6705V17.2039H5.33281C4.67007 17.2039 4.13281 16.6666 4.13281 16.0039C4.13281 15.3411 4.67007 14.8039 5.33281 14.8039H14.7994V5.33721C14.7994 4.67447 15.3367 4.13721 15.9994 4.13721Z" fill="#448181"/>
  </svg>
);

// 마이너스 아이콘 (24x24, #448181 색상)
const MinusIcon: React.FC = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M3.09961 11.9995C3.09961 11.5025 3.50255 11.0995 3.99961 11.0995H19.9995C20.4966 11.0995 20.8995 11.5025 20.8995 11.9995C20.8995 12.4966 20.4966 12.8995 19.9995 12.8995H3.99961C3.50255 12.8995 3.09961 12.4966 3.09961 11.9995Z" fill="#448181"/>
  </svg>
);

// 드래그 핸들 아이콘
const DragHandleIcon: React.FC = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ cursor: 'grab' }}>
    <circle cx="3" cy="3" r="1" fill="#ccc"/>
    <circle cx="9" cy="3" r="1" fill="#ccc"/>
    <circle cx="3" cy="6" r="1" fill="#ccc"/>
    <circle cx="9" cy="6" r="1" fill="#ccc"/>
    <circle cx="3" cy="9" r="1" fill="#ccc"/>
    <circle cx="9" cy="9" r="1" fill="#ccc"/>
  </svg>
);

// 이미지 업로드 컴포넌트
const ImageUploadComponent: React.FC<ImageUploadProps> = ({ className = "", onImageChange, imageUrl: propImageUrl }) => {
  const [imageUrl, setImageUrl] = useState<string | null>(propImageUrl || null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // propImageUrl이 변경되면 내부 상태 업데이트
  React.useEffect(() => {
    setImageUrl(propImageUrl || null);
  }, [propImageUrl]);

  const handleImageClick = () => {
    // /// console.log('Image upload clicked'); // 디버그용
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // /// console.log('File selected:', file); // 디버그용
    if (file) {
      const url = URL.createObjectURL(file);
      // /// console.log('Image URL created:', url); // 디버그용
      setImageUrl(url);
      onImageChange?.(url, file);
    }
  };

  // 배너용인지 카드용인지 구분하여 아이콘 선택
  const isBanner = className.includes('bannerImage');

  return (
    <ImageUpload className={className} onClick={handleImageClick}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />
      <ImagePlaceholder>
        {imageUrl ? (
          <UploadedImage src={imageUrl} alt="Uploaded" />
        ) : (
          <UploadIcon>
            {isBanner ? <BannerPlusIcon /> : <PlusIcon />}
          </UploadIcon>
        )}
      </ImagePlaceholder>
    </ImageUpload>
  );
};

// 입력 필드 컴포넌트
const InputFieldComponent: React.FC<InputFieldComponentProps & {
  value?: string;
  onChange?: (value: string) => void;
}> = ({ placeholder, type = "text", className = "", value = "", onChange }) => (
  <InputField className={className}>
    <Input
      type={type}
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange?.(e.target.value)}
    />
  </InputField>
);

// 버튼 컴포넌트
const Button: React.FC<ButtonProps> = ({ children, variant = "outlined", onClick, className = "", disabled = false, style }) => (
  <ButtonBase
    $variant={variant}
    $disabled={disabled}
    className={className}
    onClick={(e) => onClick?.(e)}
    disabled={disabled}
    style={style}
  >
    {children}
  </ButtonBase>
);

// 로컬 배너 타입
interface LocalBanner {
  id: string | number;
    title: string;
  text: string;
  image_url: string;
  font_color_hex_code: string;
  description?: string;
  color?: string;
  imageUrl?: string;
  imageFile?: File;
  isNew?: boolean;
  isDirty?: boolean;
}

// 배너 섹션 컴포넌트 - HomeManagementComponent와 동일한 UI
const BannerSection: React.FC = () => {
  const [banners, setBanners] = useState<LocalBanner[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 배너 목록 조회
  const loadBanners = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // /// console.log('배너 목록 조회 시작...');
      const apiBanners = await getBannerList();
      /// console.log('API에서 받은 배너 목록:', apiBanners);
      
      // API 배너를 로컬 배너 형식으로 변환 (font_color_hex_code와 image_url 필드 교체)
      const localBanners: LocalBanner[] = apiBanners.map(banner => ({
        id: banner.id,
        title: banner.title,
        text: banner.description, // API에서는 description 필드 사용
        image_url: banner.image_url,
        font_color_hex_code: banner.font_color_hex_code,
        // UI용 필드도 추가
        description: banner.description,
        color: banner.font_color_hex_code,
        imageUrl: banner.image_url,
        isNew: false,
        isDirty: false
      }));

      /// console.log('변환된 로컬 배너:', localBanners);

      setBanners(localBanners.length > 0 ? localBanners : [
        createEmptyBanner()
      ]);
    } catch (err: any) {
      /// console.error('배너 목록 조회 실패:', err);
      setError(err.message || '배너 목록을 불러올 수 없습니다.');
      // 기본 배너 하나 생성
      setBanners([createEmptyBanner()]);
    } finally {
      setLoading(false);
    }
  };

  // 빈 배너 생성 헬퍼 함수
  const createEmptyBanner = (): LocalBanner => ({
    id: `temp-${Date.now()}`,
    title: "",
    text: "",
    image_url: "",
    font_color_hex_code: "#000000",
    description: "",
    color: "#000000",
    imageUrl: "",
    isNew: true,
    isDirty: false
  });

  // 컴포넌트 마운트 시 배너 목록 조회
  useEffect(() => {
    loadBanners();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 배너 추가
  const addBanner = () => {
    if (banners.length < 4) {
      const newBanner = createEmptyBanner();
      setBanners(prev => [...prev, newBanner]);
    }
  };

  // 배너 삭제 (즉시 삭제)
  const removeBanner = async (bannerId: string | number) => {
    if (banners.length <= 1) return;

    try {
      const banner = banners.find(b => b.id === bannerId);
      if (!banner) return;

      // 서버에 저장된 배너인 경우 API 호출
      if (typeof bannerId === 'number' && !banner.isNew) {
        await deleteBanner(bannerId);
      }

      setBanners(prev => prev.filter(banner => banner.id !== bannerId));
    } catch (err: any) {
      /// console.error('배너 삭제 실패:', err);
      alert(err.message || '배너 삭제에 실패했습니다.');
    }
  };

  // 배너 업데이트
  const updateBannerLocal = (bannerId: string | number, field: string, value: string | File) => {
    /// console.log('배너 업데이트 호출:', { bannerId, field, value });
    
    setBanners(prev => prev.map(banner => {
      if (banner.id !== bannerId) return banner;

      const updates: Partial<LocalBanner> = { isDirty: true };

      if (field === 'imageFile' && value instanceof File) {
        updates.imageFile = value;
        updates.imageUrl = URL.createObjectURL(value);
        updates.image_url = URL.createObjectURL(value);
        /// console.log('이미지 파일 업데이트:', { bannerId, file: value, url: updates.imageUrl });
      } else if (typeof value === 'string') {
        if (field === 'title') {
          updates.title = value;
        } else if (field === 'description') {
          updates.description = value;
          updates.text = value;
        } else if (field === 'color') {
          updates.color = value;
          updates.font_color_hex_code = value;
        }
      }

      const updatedBanner = { ...banner, ...updates };
      /// console.log('배너 업데이트 완료:', updatedBanner);
      return updatedBanner;
    }));
  };

  // 모든 배너 저장 (일괄 저장)
  const saveAllBanners = async () => {
    try {
      setLoading(true);
      setError(null);

      // 저장할 내용이 있는지 먼저 확인
      const hasNewBanners = banners.some(banner => banner.isNew && (banner.title || banner.description || banner.imageFile));
      const hasUpdatedBanners = banners.some(banner => !banner.isNew && banner.isDirty && banner.imageFile);
      const hasTextOnlyUpdates = banners.some(banner => !banner.isNew && banner.isDirty && !banner.imageFile);

      if (!hasNewBanners && !hasUpdatedBanners) {
        if (hasTextOnlyUpdates) {
          alert('기존 배너의 제목이나 설명만 수정한 경우, 이미지도 다시 업로드해야 저장됩니다.\\n이미지를 다시 선택한 후 저장해주세요.');
          return;
        } else {
          alert('저장할 변경사항이 없습니다.');
          return;
        }
      }

      let savedCount = 0;

      for (const banner of banners) {
        // 새 배너이면서 내용이 있는 경우 생성
        if (banner.isNew && (banner.title || banner.description || banner.imageFile)) {
          if (!banner.imageFile) {
            throw new Error(`배너 "${banner.title || '제목 없음'}"의 이미지를 선택해주세요.`);
          }

          await createBanner({
            title: banner.title,
            description: banner.description || banner.text,
            imageFile: banner.imageFile,
            fontColorHexCode: banner.color || banner.font_color_hex_code
          });
          savedCount++;
        }
        // 기존 배너이면서 새 이미지가 업로드된 경우 업데이트
        else if (!banner.isNew && banner.isDirty && typeof banner.id === 'number' && banner.imageFile) {
          // console.log('배너 업데이트 시작:', {
          //   id: banner.id,
          //   title: banner.title,
          //   description: banner.description,
          //   color: banner.color,
          //   imageFile: banner.imageFile,
          //   isDirty: banner.isDirty
          // });

          await updateBanner(Number(banner.id), {
            title: banner.title,
            description: banner.description || banner.text,
            imageFile: banner.imageFile,
            fontColorHexCode: banner.color || banner.font_color_hex_code
          });
          savedCount++;
        }
      }

      // 저장 후 목록 새로고침
      await loadBanners();
      
      if (savedCount > 0) {
        alert(`${savedCount}개의 배너가 성공적으로 저장되었습니다.`);
        if (hasTextOnlyUpdates) {
          alert('참고: 이미지 없이 텍스트만 수정된 배너는 저장되지 않았습니다.\\n해당 배너의 이미지를 다시 업로드한 후 저장해주세요.');
        }
      }
    } catch (err: any) {
      /// console.error('배너 저장 실패:', err);
      setError(err.message || '배너 저장에 실패했습니다.');
      alert(err.message || '배너 저장에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const items = Array.from(banners);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setBanners(items);
  };

  return (
    <Section>
      <SectionHeader>
        <SectionTitle>배너</SectionTitle>
        {error && <ErrorMessage>{error}</ErrorMessage>}
      </SectionHeader>

      {loading && <LoadingMessage>로딩 중...</LoadingMessage>}

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="banners">
          {(provided) => (
            <div {...provided.droppableProps} style={{width: '100%'}} ref={provided.innerRef}>
      {banners.map((banner, index) => (
                <Draggable key={banner.id} draggableId={String(banner.id)} index={index}>
                  {(provided, snapshot) => (
                    <BannerContent
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      style={{
                        ...provided.draggableProps.style,
                        border: snapshot.isDragging ? '2px dashed #448181' : 'none',
                        borderRadius: snapshot.isDragging ? '8px' : '0',
                        backgroundColor: snapshot.isDragging ? '#f8f9fa' : 'transparent',
                        paddingBottom: '4px',
                        marginBottom: '20px'
                      }}
                    >

          <ImageUploadComponent
            className="bannerImage"
                        imageUrl={banner.imageUrl}
                        onImageChange={(url, file) => {
                          if (file) {
                            updateBannerLocal(banner.id, 'imageFile', file);
                          }
                        }}
          />
          <BannerForm>
            <TitleRow>
              <TitleInput>
                <InputFieldComponent
                            placeholder={`배너 ${index + 1} 제목을 입력해주세요.`}
                            value={banner.title}
                            onChange={(value) => updateBannerLocal(banner.id, 'title', value)}
                          />
              </TitleInput>
                          <ColorInputWrapper>
                            <ColorPicker
                              type="color"
                              value={banner.color || banner.font_color_hex_code}
                              onChange={(e) => updateBannerLocal(banner.id, 'color', e.target.value)}
                            />
                            <ColorTextInput
                              type="text"
                              value={banner.color || banner.font_color_hex_code}
                              onChange={(e) => {
                                const colorValue = e.target.value;
                                if (colorValue.length > 0 && !colorValue.startsWith('#')) {
                                  updateBannerLocal(banner.id, 'color', '#' + colorValue);
                                } else {
                                  if (colorValue.length <= 7) {
                                    updateBannerLocal(banner.id, 'color', colorValue);
                                  }
                                }
                              }}
                              placeholder="#000000"
                            />
              </ColorInputWrapper>
            </TitleRow>
            <CaptionRow>
              <CaptionInput>
                <InputFieldComponent
                            placeholder={`배너 ${index + 1} 설명을 입력해주세요.`}
                            value={banner.description || banner.text}
                            onChange={(value) => updateBannerLocal(banner.id, 'description', value)}
                          />
              </CaptionInput>
              <Button
                variant="outlined"
                onClick={addBanner}
                            disabled={banners.length >= 4}
              >
                <PlusIcon />
              </Button>
              <Button
                variant="outlined"
                onClick={() => removeBanner(banner.id)}
                            disabled={banners.length <= 1}
              >
                <MinusIcon />
              </Button>
            </CaptionRow>
          </BannerForm>
        </BannerContent>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {/* 배너 저장 버튼 */}
      <SectionActions>
        <SaveButton
          onClick={saveAllBanners}
          disabled={loading}
        >
          {loading ? '저장 중...' : '배너 저장'}
        </SaveButton>
      </SectionActions>
    </Section>
  );
};

// 카테고리 생성 모달 컴포넌트
const CategoryCreateModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  categories: LectureCategory[]; // 추가
}> = ({ isOpen, onClose, onSave, categories }) => {
  const [formData, setFormData] = useState({
    order: categories.length, // 기본값을 현재 카테고리 개수로 설정
    name: '',
    route_name: '',
  });

  const [homeCardImage, setHomeCardImage] = useState<File | null>(null);
  const [categoryIcon, setCategoryIcon] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);

  // 카테고리 개수가 변경될 때마다 기본 순서값 업데이트
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      order: categories.length
    }));
  }, [categories.length]);

  const handleInputChange = (field: string, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleImageChange = (type: 'homeCard' | 'icon', file: File | null) => {
    if (type === 'homeCard') {
      setHomeCardImage(file);
    } else {
      setCategoryIcon(file);
    }
  };

  const handleCreate = async () => {
    if (!formData.name || !formData.route_name) {
      alert('카테고리명과 슬러그를 입력해주세요.');
      return;
    }
    
    try {
      setLoading(true);
      
      await createLectureCategory({
        categoryName: formData.name,
        categoryRouteName: formData.route_name,
        sortOrder: formData.order,
        optimizedImage: homeCardImage || undefined,
        iconImage: categoryIcon || undefined,
      });
      
      alert('카테고리가 성공적으로 생성되었습니다.');
      onSave();
      onClose();
      
      // 폼 초기화
      setFormData({
        order: categories.length + 1, // 생성 후 다음 순서로 설정
        name: '',
        route_name: '',
      });
      setHomeCardImage(null);
      setCategoryIcon(null);
    } catch (err: any) {
      /// console.error('카테고리 생성 실패:', err);
      alert(err.message || '카테고리 생성에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // 폼 초기화
    setFormData({
      order: categories.length, // 현재 카테고리 개수로 리셋
      name: '',
      route_name: '',
    });
    setHomeCardImage(null);
    setCategoryIcon(null);
    onClose();
  };

  if (!isOpen) return null;

  // 동적으로 옵션 생성 (0부터 현재 카테고리 개수까지)
  const orderOptions = Array.from({ length: categories.length + 1 }, (_, i) => i);

  return (
    <ModalOverlay onClick={handleClose}>
      <CategoryModal onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>새 카테고리 생성</ModalTitle>
          <CloseButton onClick={handleClose}>
            <CloseIcon />
          </CloseButton>
        </ModalHeader>

        <CategoryModalBody>
          {/* 순서 */}
          <FormGroup>
            <FormLabel>순서</FormLabel>
            <SelectWrapper>
              <FormSelect
                value={formData.order}
                onChange={(e) => handleInputChange('order', parseInt(e.target.value))}
              >
                {orderOptions.map(order => (
                  <option key={order} value={order}>
                    {order} {order === categories.length ? '(마지막)' : ''}
                  </option>
                ))}
              </FormSelect>
            </SelectWrapper>
            <div>
              현재 {categories.length}개의 카테고리가 있습니다.
              새 카테고리는 기본적으로 마지막({categories.length}) 순서에 추가됩니다.
            </div>
          </FormGroup>

          {/* 카테고리명 */}
          <FormGroup>
            <FormLabel>카테고리명</FormLabel>
            <FormInput
              type="text"
              placeholder="카테고리명을 입력해주세요."
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
            />
          </FormGroup>

          {/* 슬러그 */}
          <FormGroup>
            <FormLabel>슬러그</FormLabel>
            <FormInput
              type="text"
              placeholder="(ai-basic, data-analysis, ...)"
              value={formData.route_name}
              onChange={(e) => handleInputChange('route_name', e.target.value)}
            />
          </FormGroup>

          {/* 이미지 업로드 섹션 */}
          <ImageUploadSection>
            <ImageUploadGroup>
              <FormLabel>홈 카드 이미지</FormLabel>
              <ImageUploadBox>
                <ImageUploadComponent
                  className="categoryImageUpload"
                  onImageChange={(url, file) => handleImageChange('homeCard', file || null)}
                />
              </ImageUploadBox>
            </ImageUploadGroup>

            <ImageUploadGroup>
              <FormLabel>카테고리 아이콘</FormLabel>
              <ImageUploadBox>
                <ImageUploadComponent
                  className="categoryImageUpload"
                  onImageChange={(url, file) => handleImageChange('icon', file || null)}
                />
              </ImageUploadBox>
            </ImageUploadGroup>
          </ImageUploadSection>
        </CategoryModalBody>

        <ModalFooter>
          <SaveButton
            onClick={handleCreate}
            disabled={loading}
          >
            {loading ? '생성 중...' : '카테고리 생성'}
          </SaveButton>
        </ModalFooter>
      </CategoryModal>
    </ModalOverlay>
  );
};

// 카테고리 수정 모달 컴포넌트
const CategoryEditModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  category: LectureCategory | null;
  onSave: (categoryData: Partial<LectureCategory>) => void;
  categories: LectureCategory[]; // 추가
}> = ({ isOpen, onClose, category, onSave, categories }) => {
  const [formData, setFormData] = useState({
    order: category?.sort_order || 0,
    name: category?.name || '',
    route_name: category?.route_name || '',
    parent_category_name: '',
  });

  const [homeCardImage, setHomeCardImage] = useState<File | null>(null);
  const [categoryIcon, setCategoryIcon] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [homeCardImageUrl, setHomeCardImageUrl] = useState<string | null>(null);
  const [categoryIconUrl, setCategoryIconUrl] = useState<string | null>(null);

  // 카테고리가 변경될 때 폼 데이터 업데이트
  useEffect(() => {
    if (category) {
      setFormData({
        order: category.sort_order,
        name: category.name,
        route_name: category.route_name,
        parent_category_name: '',
      });
      
      // 기존 이미지 URL 설정
      setHomeCardImageUrl(category.optimized_image_url || null);
      setCategoryIconUrl(category.icon_image_url || null);
      
      // 새로 업로드할 파일 초기화
      setHomeCardImage(null);
      setCategoryIcon(null);
    }
  }, [category]);

  const handleInputChange = (field: string, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleImageChange = (type: 'homeCard' | 'icon', file: File | null) => {
    if (type === 'homeCard') {
      setHomeCardImage(file);
      if (file) {
        // 새 파일이 선택되면 URL도 업데이트
        setHomeCardImageUrl(URL.createObjectURL(file));
      }
    } else {
      setCategoryIcon(file);
      if (file) {
        // 새 파일이 선택되면 URL도 업데이트
        setCategoryIconUrl(URL.createObjectURL(file));
      }
    }
  };

  const handleSave = async () => {
    if (!category) return;
    
    try {
      setLoading(true);
      
      await updateLectureCategory(category.id, {
        categoryName: formData.name,
        categoryRouteName: formData.route_name,
        parentCategoryId: category.parent_category_id || undefined,
        sortOrder: formData.order,
        optimizedImage: homeCardImage || undefined,
        iconImage: categoryIcon || undefined,
      });
      
      alert('카테고리가 성공적으로 수정되었습니다.');
      onSave(formData);
      onClose();
    } catch (err: any) {
      /// console.error('카테고리 수정 실패:', err);
      alert(err.message || '카테고리 수정에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // 이미지 URL 초기화
    setHomeCardImageUrl(null);
    setCategoryIconUrl(null);
    onClose();
  };

  const handleDelete = async () => {
    if (!category) return;
    
    if (!window.confirm(`"${category.name}" 카테고리를 삭제하시겠습니까?\n\n이 카테고리에 강의가 있는 경우 삭제할 수 없습니다.`)) {
      return;
    }
    
    try {
      setLoading(true);
      
      await deleteLectureCategory(category.id);
      
      alert('카테고리가 성공적으로 삭제되었습니다.');
      onClose();
    } catch (err: any) {
      /// console.error('카테고리 삭제 실패:', err);
      alert(err.message || '카테고리 삭제에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  // 동적으로 옵션 생성 (0부터 현재 카테고리 개수-1까지)
  const orderOptions = Array.from({ length: categories.length }, (_, i) => i);

  return (
    <ModalOverlay onClick={handleClose}>
      <CategoryModal onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>클래스 카테고리 관리</ModalTitle>
          <CloseButton onClick={handleClose}>
            <CloseIcon />
          </CloseButton>
        </ModalHeader>

        <CategoryModalBody>
          {/* 순서 */}
          <FormGroup>
            <FormLabel>순서</FormLabel>
            <SelectWrapper>
              <FormSelect
                value={formData.order}
                onChange={(e) => handleInputChange('order', parseInt(e.target.value))}
              >
                {orderOptions.map(order => (
                  <option key={order} value={order}>
                    {order}
                  </option>
                ))}
              </FormSelect>
            </SelectWrapper>
            <div>
              전체 {categories.length}개 카테고리 중 순서를 선택하세요 (0부터 시작)
            </div>
          </FormGroup>

          {/* 카테고리명 */}
          <FormGroup>
            <FormLabel>카테고리명</FormLabel>
            <FormInput
              type="text"
              placeholder="카테고리명을 입력해주세요."
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
            />
          </FormGroup>

          {/* 상위 카테고리 */}
          <FormGroup>
            <FormLabel>상위 카테고리</FormLabel>
            <FormInput
              type="text"
              $disabled
              placeholder="상위 카테고리 이름"
              value={formData.parent_category_name}
              disabled
            />
          </FormGroup>

          {/* 슬러그 */}
          <FormGroup>
            <FormLabel>슬러그</FormLabel>
            <FormInput
              type="text"
              placeholder="(ai-basic, data-analysis, ...)"
              value={formData.route_name}
              onChange={(e) => handleInputChange('route_name', e.target.value)}
            />
          </FormGroup>

          {/* 이미지 업로드 섹션 */}
          <ImageUploadSection>
            <ImageUploadGroup>
              <FormLabel>홈 카드 이미지</FormLabel>
              <ImageUploadBox>
                <ImageUploadComponent
                  className="categoryImageUpload"
                  imageUrl={homeCardImageUrl || undefined}
                  onImageChange={(url, file) => handleImageChange('homeCard', file || null)}
                />
              </ImageUploadBox>
            </ImageUploadGroup>

            <ImageUploadGroup>
              <FormLabel>카테고리 아이콘</FormLabel>
              <ImageUploadBox>
                <ImageUploadComponent
                  className="categoryImageUpload"
                  imageUrl={categoryIconUrl || undefined}
                  onImageChange={(url, file) => handleImageChange('icon', file || null)}
                />
              </ImageUploadBox>
            </ImageUploadGroup>
          </ImageUploadSection>
        </CategoryModalBody>

        <ModalFooter>
          <DeleteButton
            onClick={handleDelete}
            disabled={loading}
          >
            {loading ? '삭제 중...' : '삭제'}
          </DeleteButton>
          <SaveButton
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? '저장 중...' : '저장하기'}
          </SaveButton>
        </ModalFooter>
      </CategoryModal>
    </ModalOverlay>
  );
};

// 닫기 아이콘 컴포넌트
const CloseIcon: React.FC = () => (
  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
    <path d="M21 7L7 21M7 7L21 21" stroke="#171719" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// 카테고리 테이블 섹션
const CategorySection: React.FC<{
  categories: LectureCategory[];
  onRefresh?: () => void;
}> = ({ categories, onRefresh }) => {
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<LectureCategory | null>(null);
  const [localCategories, setLocalCategories] = useState<LectureCategory[]>(categories);

  // categories가 변경될 때 로컬 상태 업데이트
  useEffect(() => {
    setLocalCategories(categories);
  }, [categories]);

  const handleEditClick = (category: LectureCategory, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setSelectedCategory(category);
    setEditModalOpen(true);
  };

  const handleModalClose = () => {
    setEditModalOpen(false);
    setSelectedCategory(null);
  };

  const handleCreateModalClose = () => {
    setCreateModalOpen(false);
  };

  const handleCreateCategory = () => {
    onRefresh?.();
    handleCreateModalClose();
  };

  const handleDeleteClick = async (category: LectureCategory, e?: React.MouseEvent) => {
    e?.stopPropagation();
    
    if (!window.confirm(`"${category.name}" 카테고리를 삭제하시겠습니까?\n\n이 카테고리에 강의가 있는 경우 삭제할 수 없습니다.`)) {
      return;
    }
    
    try {
      await deleteLectureCategory(category.id);
      alert('카테고리가 성공적으로 삭제되었습니다.');
      onRefresh?.();
    } catch (err: any) {
      /// console.error('카테고리 삭제 실패:', err);
      alert(err.message || '카테고리 삭제에 실패했습니다.');
    }
  };

  const handleSaveCategory = (categoryData: Partial<LectureCategory>) => {
    /// console.log('카테고리 저장:', categoryData);
    // 카테고리 목록 새로고침
    onRefresh?.();
    handleModalClose();
  };

  // 드래그 앤 드롭 핸들러
  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination) return;

    const items = Array.from(localCategories);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // 새로운 순서로 sort_order 업데이트
    const updatedItems = items.map((item, index) => ({
      ...item,
      sort_order: index
    }));

    // 로컬 상태 즉시 업데이트 (UI 반응성을 위해)
    setLocalCategories(updatedItems);

    try {
      // TODO: 데이터 정리가 완료될 때까지 드래그앤드롭 비활성화
      /// console.warn('카테고리 데이터 정리가 필요합니다. 드래그앤드롭을 임시 비활성화합니다.');
      alert('카테고리 데이터에 문제가 있어 순서 변경이 비활성화되었습니다.\n\n해결 방법:\n1. 존재하지 않는 카테고리들 삭제 (21, 22, 23, 24번)\n2. 중복된 이름의 카테고리들 수정 (13, 20번)\n3. 관리자에게 문의하세요.');
      
      // 실패 시 원래 상태로 되돌리기
      setLocalCategories(categories);
      return;
      
      // 아래 코드는 데이터 정리 후 활성화
      /*
      // 서버에 순서 변경 요청 (1부터 시작, name과 route_name 포함)
      const orderUpdates = updatedItems.map((item, index) => ({
        id: item.id,
        sortOrder: index + 1,
        name: item.name,
        slug: item.route_name
      }));

      await updateCategoriesOrder(orderUpdates);
      
      // 성공 시 전체 목록 새로고침
      onRefresh?.();
      */
    } catch (err: any) {
      /// console.error('카테고리 순서 업데이트 실패:', err);
      alert(err.message || '카테고리 순서 업데이트에 실패했습니다.');
      
      // 실패 시 원래 상태로 되돌리기
      setLocalCategories(categories);
    }
  };

  // 드래그 핸들 아이콘 - 임시 비활성화
  // const DragHandleIcon: React.FC = () => (
  //   <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ cursor: 'grab' }}>
  //     <circle cx="3" cy="3" r="1" fill="#ccc"/>
  //     <circle cx="9" cy="3" r="1" fill="#ccc"/>
  //     <circle cx="3" cy="6" r="1" fill="#ccc"/>
  //     <circle cx="9" cy="6" r="1" fill="#ccc"/>
  //     <circle cx="3" cy="9" r="1" fill="#ccc"/>
  //     <circle cx="9" cy="9" r="1" fill="#ccc"/>
  //   </svg>
  // );

  return (
    <>
    <CategorySectionContainer>
      <SectionHeader>
        <SectionTitle>카테고리</SectionTitle>
        <div>
          {/* 카테고리를 드래그하여 순서를 변경할 수 있습니다 */}
        </div>
      </SectionHeader>
      <TableContainer>
        <TableInner>
        <TableHead>
          <TableHeadRow>
            {/* 드래그 컬럼 제거 */}
            <CellOrder $isHeader>
              <CellContainer>
                <HeaderCellText>순서</HeaderCellText>
              </CellContainer>
            </CellOrder>
            <CellName $isHeader>
              <CellContainer>
                <HeaderCellText>카테고리명</HeaderCellText>
              </CellContainer>
            </CellName>
            <CellParent $isHeader>
              <CellContainer>
                <HeaderCellText>상위 카테고리</HeaderCellText>
              </CellContainer>
            </CellParent>
            <CellCount $isHeader>
              <CellContainer>
                <HeaderCellText>강의 수</HeaderCellText>
              </CellContainer>
            </CellCount>
            <CellAction $isHeader>
              <CellContainer>
                <HeaderCellText>관리</HeaderCellText>
              </CellContainer>
            </CellAction>
          </TableHeadRow>
        </TableHead>

        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="categories">
            {(provided) => (
              <TableBody
                {...provided.droppableProps}
                ref={provided.innerRef}
              >
                {localCategories.map((category, index) => (
                  <Draggable key={category.id} draggableId={String(category.id)} index={index}>
                    {(provided, snapshot) => (
                      <TableBodyRow
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        style={{
                          ...provided.draggableProps.style,
                          border: snapshot.isDragging ? '2px dashed #448181' : 'none',
                          borderRadius: snapshot.isDragging ? '8px' : '0',
                          backgroundColor: snapshot.isDragging ? '#f8f9fa' : 'transparent',
                        }}
                      >
                        {/* 드래그 셀 완전 제거 */}
                        <CellOrder>
                          <CellContainer>
                            <CellText>{category.sort_order}</CellText>
                          </CellContainer>
                        </CellOrder>
                        <CellName>
                          <CellContainer>
                            <CellText>{category.name}</CellText>
                          </CellContainer>
                        </CellName>
                        <CellParent>
                          <CellContainer>
                            <CellText>{category.parent_category_name ?? '-'}</CellText>
                          </CellContainer>
                        </CellParent>
                        <CellCount>
                          <CellContainer>
                            <CellText>{
                              typeof category.lecture_count === 'number' ? category.lecture_count : '-'
                            }</CellText>
                          </CellContainer>
                        </CellCount>
                        <CellAction>
                          <TableButton
                            onClick={(e) => handleEditClick(category, e)}
                          >
                            수정
                          </TableButton>
                          <TableButtonDelete
                            onClick={(e) => handleDeleteClick(category, e)}
                          >
                            삭제
                          </TableButtonDelete>
                        </CellAction>
                      </TableBodyRow>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </TableBody>
            )}
          </Droppable>
        </DragDropContext>

        {/* 카테고리 추가 버튼 */}
        <AddCategoryButton onClick={() => setCreateModalOpen(true)}>
          <PlusIcon />
        </AddCategoryButton>
        </TableInner>
      </TableContainer>
    </CategorySectionContainer>

      <CategoryEditModal
        isOpen={editModalOpen}
        onClose={handleModalClose}
        category={selectedCategory}
        onSave={handleSaveCategory}
        categories={categories}
      />

      <CategoryCreateModal
        isOpen={createModalOpen}
        onClose={handleCreateModalClose}
        onSave={handleCreateCategory}
        categories={categories}
      />
    </>
  );
};


// 큐레이션 관련 타입
interface LectureCurationSet {
  id: string | number;
  title: string;
  description: string;
  lectures: {
    id: number;
    relation_id?: number;
    title: string;
    thumbnail_url: string;
    lecturer_name?: string;
    lecturer_department?: string;
    lecturer_id?: string;
  }[];
  isNew?: boolean;
  isDirty?: boolean;
  isExpanded?: boolean;
}

// 강의 큐레이션 관리 컴포넌트
const ClassCurationManagement: React.FC = () => {
  const [curationSets, setCurationSets] = useState<LectureCurationSet[]>([]);
  const [availableLectures, setAvailableLectures] = useState<LectureListItemType[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lectureModalOpen, setLectureModalOpen] = useState<string | null>(null);
  
  // 새 큐레이션 생성용 폼 상태
  const [newCurationForm, setNewCurationForm] = useState({
    title: '',
    description: ''
  });

  // 강의 목록 조회
  const loadLectures = async () => {
    try {
      const lectureResponse = await getLectureList(0, 100);
      setAvailableLectures(lectureResponse.content);
    } catch (err: any) {
      /// console.error('강의 목록 조회 실패:', err);
    }
  };

  // 큐레이션 목록 조회
  const loadCurations = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const curations = await getLectureCurationList();
      
      // API 데이터를 로컬 형식으로 변환
      const localCurations: LectureCurationSet[] = curations.map((curation: any) => ({
        id: curation.lecture_section_id,
        title: curation.title,
        description: curation.text,
        lectures: (curation.related_lectures || []).map((lecture: any) => ({
          id: lecture.lecture_id,
          relation_id: lecture.relation_id,
          title: lecture.title,
          thumbnail_url: lecture.thumbnail_url,
          lecturer_name: lecture.lecturer_info.name || '강연자명 없음',
          lecturer_department: lecture.lecturer_info.department || '소개 없음',
          lecturer_id: lecture.lecturer_info.id || '강연자ID 없음'
        })),
        isNew: false,
        isDirty: false
      }));

      setCurationSets(localCurations.length > 0 ? localCurations : [createEmptyCuration()]);
    } catch (err: any) {
      /// console.error('큐레이션 목록 조회 실패:', err);
      setError(err.message || '큐레이션 목록을 불러올 수 없습니다.');
      setCurationSets([createEmptyCuration()]);
    } finally {
      setLoading(false);
    }
  };

  // 빈 큐레이션 생성
  const createEmptyCuration = (): LectureCurationSet => ({
    id: `temp-${Date.now()}`,
    title: '',
    description: '',
    lectures: [],
    isNew: true,
    isDirty: false
  });

  // 컴포넌트 마운트 시 데이터 로드
  useEffect(() => {
    loadLectures();
    loadCurations();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 큐레이션 세트 추가 (바로 저장)
  const addCurationSet = async () => {
    if (!newCurationForm.title.trim()) {
      alert('큐레이션 제목을 입력해주세요.');
      return;
    }
    
    try {
      setLoading(true);
      
      // 서버에 바로 저장
      const result = await createLectureCurationSection({
        title: newCurationForm.title,
        description: newCurationForm.description
      });
      
      // 로컬 상태에 추가 (서버에서 받은 ID 사용)
      const newSet: LectureCurationSet = {
        id: result.id,
        title: newCurationForm.title,
        description: newCurationForm.description,
        lectures: [],
        isNew: false,
        isDirty: false
      };
      
      setCurationSets(prev => [...prev, newSet]);
      
      // 폼 초기화
      setNewCurationForm({
        title: '',
        description: ''
      });
      
      alert('큐레이션이 생성되었습니다.');
    } catch (err: any) {
      /// console.error('큐레이션 생성 실패:', err);
      alert(err.message || '큐레이션 생성에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 새 큐레이션 폼 입력 핸들러
  const handleNewCurationInputChange = (field: 'title' | 'description', value: string) => {
    setNewCurationForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // 기존 큐레이션 섹션 편집 모드 상태
  const [editingCurationId, setEditingCurationId] = useState<string | number | null>(null);
  const [editingForm, setEditingForm] = useState({
    title: '',
    description: ''
  });

  // 큐레이션 섹션 편집 시작
  const startEditingCuration = (curationSet: LectureCurationSet) => {
    setEditingCurationId(curationSet.id);
    setEditingForm({
      title: curationSet.title,
      description: curationSet.description
    });
  };

  // 큐레이션 섹션 편집 취소
  const cancelEditingCuration = () => {
    setEditingCurationId(null);
    setEditingForm({
      title: '',
      description: ''
    });
  };

  // 큐레이션 섹션 편집 저장
  const saveEditingCuration = () => {
    if (!editingCurationId) return;
    
    updateCurationSet(editingCurationId, 'title', editingForm.title);
    updateCurationSet(editingCurationId, 'description', editingForm.description);
    
    setEditingCurationId(null);
    setEditingForm({
      title: '',
      description: ''
    });
  };

  // 편집 폼 입력 핸들러
  const handleEditingInputChange = (field: 'title' | 'description', value: string) => {
    setEditingForm(prev => ({
        ...prev,
      [field]: value
    }));
  };

  // 큐레이션 세트 제거
  const removeCurationSet = async (setId: string | number) => {
    if (curationSets.length <= 1) return;

    try {
      const curation = curationSets.find(c => c.id === setId);
      if (!curation) return;

      // 서버에 저장된 큐레이션인 경우 API 호출
      if (typeof setId === 'number' && !curation.isNew) {
        // 연결된 강의 먼저 해제 후 삭제 (연결 있으면 500 발생)
        for (const lecture of curation.lectures) {
          await removeLectureFromCuration(setId, lecture.id);
        }
        await deleteLectureCurationSection(setId);
      }

      setCurationSets(prev => prev.filter(set => set.id !== setId));
    } catch (err: any) {
      /// console.error('큐레이션 섹션 삭제 실패:', err);
      alert(err.message || '큐레이션 섹션 삭제에 실패했습니다.');
    }
  };

  // 큐레이션 세트 업데이트
  const updateCurationSet = (setId: string | number, field: 'title' | 'description', value: string) => {
    setCurationSets(prev => prev.map(set => {
      if (set.id !== setId) return set;
      
      const currentValue = field === 'title' ? set.title : set.description;
      if (currentValue === value) return set;
      
      return { 
        ...set, 
        [field]: value, 
        isDirty: true
      };
    }));
  };

  // 강의를 큐레이션에 추가
  const addLectureToCurationSet = async (setId: string | number, lecture: any) => {
    try {
      const curation = curationSets.find(c => c.id === setId);
      if (!curation) return;

      // 이미 추가된 강의인지 확인
      if (curation.lectures.some(l => l.id === lecture.lecture_info.id)) {
        alert('이미 추가된 강의입니다.');
        return;
      }

      // 큐레이션에 강의 추가 (새 API 사용)
      await addLectureToCuration(Number(setId), lecture.lecture_info.id);

      // 로컬 상태 업데이트
      setCurationSets(prev => prev.map(c => {
        if (c.id !== setId) return c;
        return {
          ...c,
          lectures: [...c.lectures, {
            id: lecture.lecture_info.id,
            title: lecture.lecture_info.title,
            thumbnail_url: lecture.lecture_info.thumbnail_url,
            lecturer_name: lecture.lecturer_info?.name || '강연자명 없음'
          }],
          isDirty: true
        };
      }));

      setLectureModalOpen(null);
    } catch (err: any) {
      /// console.error('강의 추가 실패:', err);
      alert(err.message || '강의 추가에 실패했습니다.');
    }
  };

  // 강의를 큐레이션에서 제거
  const removeLectureFromCurationSet = async (setId: string | number, lectureId: number) => {
    try {
      const curation = curationSets.find(c => c.id === setId);
      if (!curation) return;

      const lecture = curation.lectures.find(l => l.id === lectureId);
      if (!lecture) return;

      // 서버에서 제거 (새 API 사용)
      if (typeof setId === 'number') {
        await removeLectureFromCuration(setId, lectureId);
      }

      // 로컬 상태 업데이트
      setCurationSets(prev => prev.map(c => {
        if (c.id !== setId) return c;
        return {
          ...c,
          lectures: c.lectures.filter(l => l.id !== lectureId),
          isDirty: true
        };
      }));
    } catch (err: any) {
      /// console.error('강의 제거 실패:', err);
      alert(err.message || '강의 제거에 실패했습니다.');
    }
  };


  return (
    <Section>
      <SectionHeader>
        <SectionTitle>클래스 큐레이션</SectionTitle>
        {error && <ErrorMessage>{error}</ErrorMessage>}
      </SectionHeader>

      {loading && <LoadingMessage>로딩 중...</LoadingMessage>}

      {/* 큐레이션 생성 폼 */}
      <CurationForm>
        <CurationFormGroup>
          <InputFieldComponent
            placeholder="큐레이션 제목을 입력해주세요."
            value={newCurationForm.title}
            onChange={(value) => handleNewCurationInputChange('title', value)}
          />
          <InputFieldComponent
            placeholder="큐레이션 설명을 입력해주세요."
            value={newCurationForm.description}
            onChange={(value) => handleNewCurationInputChange('description', value)}
          />
        </CurationFormGroup>
        <CurationButtons>
          <Button
            variant="outlined"
            onClick={addCurationSet}
            disabled={curationSets.length >= 4}
          >
            <PlusIcon />
          </Button>
        </CurationButtons>
      </CurationForm>

      {curationSets.map((curationSet) => (
        <CurationSet key={curationSet.id}>
          {/* 큐레이션 섹션 헤더 */}
          <CurationSectionHeader>
            <div style={{ flex: 1 }}>
              {editingCurationId === curationSet.id ? (
                // 편집 모드
                <CurationFormGroup>
                  <InputFieldComponent
                    placeholder="큐레이션 제목을 입력해주세요."
                    value={editingForm.title}
                    onChange={(value) => handleEditingInputChange('title', value)}
                  />
                  <InputFieldComponent
                    placeholder="큐레이션 설명을 입력해주세요."
                    value={editingForm.description}
                    onChange={(value) => handleEditingInputChange('description', value)}
                  />
                </CurationFormGroup>
              ) : (
                // 표시 모드
                <div>
                  <CurationSectionTitle>{curationSet.title || '제목 없음'}</CurationSectionTitle>
                  <CurationSectionDescription>{curationSet.description || '설명 없음'}</CurationSectionDescription>
                </div>
              )}
            </div>
            <CurationSectionActions>
              {editingCurationId === curationSet.id ? (
                // 편집 모드 버튼들
                <>
                  <Button
                    variant="outlined"
                    onClick={saveEditingCuration}
                  >
                    저장
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={cancelEditingCuration}
                  >
                    취소
                  </Button>
                </>
              ) : (
                // 표시 모드 버튼들
                <>
                  <Button
                    variant="outlined"
                    onClick={() => startEditingCuration(curationSet)}
                  >
                    편집
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => removeCurationSet(curationSet.id)}
                    disabled={curationSets.length <= 1}
                  >
                    <MinusIcon />
                  </Button>
                </>
              )}
            </CurationSectionActions>
          </CurationSectionHeader>

          {/* 강의 카드 그리드 */}
          <LectureGrid>
            {curationSet.lectures.map((lecture) => (
              <LectureCard key={lecture.id}>
                <LectureCardImage>
                  {lecture.thumbnail_url ? (
                    <img src={lecture.thumbnail_url} alt={lecture.title} />
                  ) : (
                    <NoImage>이미지 없음</NoImage>
                  )}
                </LectureCardImage>
                <LectureCardContent>
                  <LectureTitle>{lecture.title}</LectureTitle>
                  {lecture.lecturer_name && (
                    <LecturerName>{lecture.lecturer_name} · {lecture.lecturer_department}</LecturerName>
                  )}
                </LectureCardContent>
                <RemoveLectureButton
                  onClick={() => removeLectureFromCurationSet(curationSet.id, lecture.id)}
                >
                  <MinusIcon />
                </RemoveLectureButton>
              </LectureCard>
            ))}

            {/* 강의 추가 버튼 */}
            <AddLectureCard
              onClick={() => setLectureModalOpen(String(curationSet.id))}
              title="강의 추가"
            >
              <PlusIcon />
              <span>강의 추가</span>
            </AddLectureCard>
          </LectureGrid>

          {/* 강의 선택 모달 */}
          {lectureModalOpen === String(curationSet.id) && (
            <LectureModal>
              <ModalOverlay onClick={() => setLectureModalOpen(null)} />
              <ModalContent>
                <ModalHeader>
                  <h3>강의 선택</h3>
                  <button onClick={() => setLectureModalOpen(null)}>×</button>
                </ModalHeader>
                <ModalBody>
                  <LectureList>
                    {availableLectures.map((lecture) => {
                      const isAlreadyAdded = curationSet.lectures.some(l => l.id === lecture.lecture_info.id);

                      return (
                        <LectureListItem
                          key={lecture.lecture_info.id}
                          $alreadyAdded={isAlreadyAdded}
                          onClick={() => {
                            if (isAlreadyAdded) {
                              alert('이 강의는 이미 추가되어 있습니다.');
                              return;
                            }
                            addLectureToCurationSet(curationSet.id, lecture);
                          }}
                          title={isAlreadyAdded ? '이미 추가된 강의' : '클릭하여 추가'}
                        >
                          <LectureListImage>
                            {lecture.lecture_info.thumbnail_url ? (
                              <img src={lecture.lecture_info.thumbnail_url} alt={lecture.lecture_info.title} />
                            ) : (
                              <NoImage>이미지 없음</NoImage>
                            )}
                          </LectureListImage>
                          <LectureListContent>
                            <h4>{lecture.lecture_info.title}</h4>
                            <p>{lecture.lecturer_info?.name || '강연자명 없음'} · {lecture.lecturer_info?.department || '소개 없음'}</p>
                            <LectureTags>
                              {lecture.lecture_info.tags?.map((tag: string, idx: number) => (
                                <Tag key={idx}>{tag}</Tag>
                              )) || []}
                            </LectureTags>
                          </LectureListContent>
                          {isAlreadyAdded && (
                            <AddedIndicator>
                              ✓ 추가됨
                            </AddedIndicator>
                          )}
                        </LectureListItem>
                      );
                    })}
                  </LectureList>
                </ModalBody>
              </ModalContent>
            </LectureModal>
          )}
        </CurationSet>
      ))}

    </Section>
  );
};


const ClassManagementComponent: React.FC = () => {
  // 상태 관리
  const [categories, setCategories] = useState<LectureCategory[]>([]);
  const [loading, setLoading] = useState(true);


  // 초기 데이터 로드
  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const categoriesData = await getLectureCategories();
      setCategories(categoriesData);
    } catch (err: any) {
      /// console.error('초기 데이터 로드 실패:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <PageHeader>
          <PageTitle>클래스 관리</PageTitle>
        </PageHeader>
        <PageContent>
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            로딩 중...
          </div>
        </PageContent>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>클래스 관리</PageTitle>
      </PageHeader>

      <PageContent>
        <BannerSection />
        <CategorySection
          categories={categories}
          onRefresh={loadInitialData}
        />
        <ClassCurationManagement />
      </PageContent>
    </PageContainer>
  );
};

export default ClassManagementComponent;
